(ns dashboard.entity.organization.profile
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require
   ["react" :as react]
   [clojure.set]
   [clojure.string :as str]
   [dashboard.common :as common]
   [dashboard.components.keybindings :refer [keybindings]]
   [dashboard.entity.analysis.profile :as analysis.profile]
   [io.repliance.schemas.organization :as org]
   [reitit.frontend.easy :as rfe]))

(defn td-with-diff
  "Displays a table cell, with the visual demonstration of the diff from the
   previous state being annotated in green"
  [data previous col-key & [transformer]]
  (let [transformer (or transformer identity)]
    (if (and (seq previous)
             (not= (get data col-key)
                   (get previous col-key)))
      [:td.is-success.organization-revision-diff-cell
       (transformer (get data col-key))
       [:i.fas.fa-arrow-down-up-across-line.organization-revision-diff-arrow]]
      [:td (transformer (get data col-key))])))

(defn revision-table-header
  "Draws the header for the person's revision history table"
  []
  [:thead
   [:tr
    [:th "Revision"]
    [:th "Saved"]
    [:th "Full Name"]
    [:th "Title"]
    [:th "Reports To"]
    [:th "Started"]
    [:th "Ended"]
    [:th "Active"]
    [:th "Edited By"]]])

(defn revision-line-item
  "Draws an individual line-item in the person's revision history table"
  [data person-names idx {:keys [revision
                                 author
                                 reports-to-person-id
                                 saved-ts]
                          :as row}]
  (let [previous (when (< idx (dec (count data)))
                   (get-in data [(inc idx)]))]
    [:tr
     [:td revision]
     [:td (common/format-date-time saved-ts)]
     [td-with-diff row previous :fullname]
     [td-with-diff row previous :title]
     [td-with-diff
      {:name (get person-names reports-to-person-id)}
      (when previous
        {:name (get person-names (:reports-to-person-id previous))})
      :name
      str]
     [td-with-diff row previous :start-ts common/format-date->input]
     [td-with-diff row previous :end-ts common/format-date->input]
     [td-with-diff row previous :is-active str]
     [:td (str author)]]))


(defn revision-history-modal
  "Displays a table of all previous version of the person, with a visual diff
   of the changes visuall annotated in green"
  [{:keys [data person-id person-names perform-navigation member-view]}]
  (let [data (->> data
                  (sort-by :revision)
                  reverse
                  vec)
        on-close #(perform-navigation member-view {:selected-person-id person-id})]
    [:div.modal.is-active.organization-person-revision-history-wrapper
     {:key :modal-organization-person-revision-history}
     [:div.modal-background
      {:on-click on-close
       :style {:overflow "scroll"}}]
     [:f> keybindings
      {:actions (merge {"Escape" on-close})}
      [:div.is-flex-direction-column
       [:div.notification.organization-person-revision-history-modal
        [:a.delete.is-rounded
         {:href (rfe/href member-view {:selected-person-id person-id})}]
        [:div.level.level-left.title
         [:span.icon>i.fas.fa-timeline.ml-3]
         [:span.ml-5
          "Revision History for "
          (-> data first :fullname)]]
        [:div.table-wrapper
         [:table.table.is-fullwidth
          [revision-table-header]
          (->> data
               (map-indexed (partial revision-line-item
                                     data
                                     person-names))
               (into [:tbody]))]]]]]]))

(defn responsibilities-modal
  "Displays a table of all previous version of the person, with a visual diff
   of the changes visuall annotated in green"
  [{:keys [data analyses on-close close-href person on-open-modal]}]
  (let [asa-versions (->> data (map :version-id) set)
        sa->asa (->> analyses
                     (map
                      (fn [{:keys [applied-analyses version-id]}]
                        {:version-id version-id
                         :asa-version-ids (->> applied-analyses
                                               (mapv
                                                (fn [{:keys [version-id]}] version-id))
                                               set)}))
                     (keep
                      (fn [{:keys [asa-version-ids version-id]}]
                        (when (seq (clojure.set/intersection asa-version-ids asa-versions))
                          version-id)))
                     set)
        [selected-construct set-selected-construct] (react/useState nil)]
    [:div.notification.organization-person-revision-history-modal
     [:a.delete.is-rounded
      {:href close-href
       :on-click (fn [_]
                   (on-close))}]
     [:div.level.level-left.title
      [:span.icon>i.fas.fa-magnifying-glass-chart.ml-3]
      [:span.ml-5 "Responsibilities for "
       (:fullname person)]]
     [:div.notification.is-info.level
      [:i.fas.fa-info-circle.is-large.icon.mr-4]
      [:div
       [:p
        "Responsibilitiy come from assigning an organization member as a "
        [:b "`Directly Responsible Individual`"]
        " in the "
        [:b "Responsible Owner"]
        " section of an Applied Analysis. "
        "An organization member can appear in multiple versions of an analysis.
                   Select an analysis construct to reveal which versions "
        [:b (:fullname person)]
        " was a responsible party for."]]]
     (->> analyses
          (filter (fn [{:keys [version-id]}]
                    (contains? sa->asa version-id)))
          (sort-by :construct-id)
          (mapv (fn [{:keys [construct applied-analyses construct-id states]
                      :as structure}]
                  [:<>
                   [:a
                    {:class [:level :mb-1 :box
                             :analysis-list-row
                             :hierarchy-row :clickable-row
                             :organization-responsibility-item]
                     :on-click  (fn [e]
                                  (.preventDefault e)
                                  (if (= selected-construct structure)
                                    (set-selected-construct nil)
                                    (set-selected-construct structure)))
                     :href "#"
                     :tab-index 1}
                    [:span.label
                     (analysis.profile/format-analysis-id construct-id)
                     " - "
                     construct]]
                   (when (= selected-construct structure)
                     [:div.section.pt-2
                      [:div.analysis-list
                       (->> applied-analyses
                            (mapv
                             (fn [{:keys [version-id version state-id author created]
                                   :as analysis}]
                               (when (contains? asa-versions version-id)
                                 (let [state-id->state-name (into {} states)]
                                   [:tr.analysis-list-row.hierarchy-row.clickable-row
                                    [:td.ml-2
                                     [:button.analysis-list-table-row-button
                                      {:on-click #(on-open-modal construct-id :view analysis)
                                       :tab-index 1}]
                                     (str "v" version)]
                                    [:td.ml-2 (str author)]
                                    [:td.ml-2 (str created)]
                                    [:td.ml-2 (state-id->state-name state-id)]]))))
                            (into [:tbody])
                            vector
                            (into [:table.table.is-fullwidth
                                   [:thead
                                    [:tr
                                     [:th.ml-2 "Analysis Version"]
                                     [:th.ml-2 "Author"]
                                     [:th.ml-2 "Timestamp"]
                                     [:th.ml-2 "State"]]]]))]])]))
          (into [:div.analysis-list]))]))

(defn member-attribute-wrapper
  "Generic Wrapper for an element in the member/person profile card"
  [display-name component]
  [:div.level.organization-member-attribute
   [:div.organization-member-attribute-name.level-left
    display-name
    ": "]
   [:div.level-right
    component]])

(defn member-attribute
  "Text Input UI for an element in the member/person profile card"
  [display-name value on-change form-error]
  [member-attribute-wrapper
   display-name
   [:<>
    [:input.input
     {:value value
      :on-change #(-> % .-target .-value on-change)
      :id (gensym)}]
    (when form-error
      [:div.help.is-danger (str (first form-error))])]])

(defn member-select
  "Person select control in the member/person profile card. Accepts
   excluded-report-ids as a set of IDs to not be available as a choice in the
   select. That list should include the person's ID and all of their reports
   below them in the hierarchy."
  [members excluded-report-ids value on-change disabled]
  (when excluded-report-ids
    (->> members
         (filter (comp not (set (or excluded-report-ids nil)) :person-id))
         (sort-by (comp str/lower-case :fullname))
         (mapv (fn [{:keys [fullname person-id title]}]
                 [:option
                  {:value person-id}
                  (str fullname (when (seq title) " - ") title)]))
         (into [:select
                {:value (-> (filter (comp (partial = value) :person-id) members)
                            first
                            :person-id
                            (or 0))
                 :id (gensym)
                 :disabled disabled
                 :on-change #(when on-change
                               (-> %
                                   .-target
                                   .-value
                                   js/parseInt
                                   on-change))}]))))

(defn member-date-input
  "Date Input UI for an element in the member/person profile card"
  [display-name value on-change minimum maximum]
  [member-attribute-wrapper
   display-name
   [:input
    {:type "date"
     :value (common/format-date->input value)
     :id (gensym)
     :min (common/format-date->input minimum)
     :max (common/format-date->input maximum)
     :on-change #(-> %
                     .-target
                     .-value
                     common/format-input->date
                     on-change)}]])

(defn member-profile
  "Displays the details of a person within an organization"
  [{:keys [person-id
           fullname
           title
           reports-to-person-id
           reports
           start-ts
           end-ts
           ;; ASA/external Form Props
           prevent-navigate-on-save
           abbridged-form
           on-close]
    :as member}
   on-submit
   members
   form-errors
   additional-actions
   ref
   organization-view]
  (let [[new-name set-name] (react/useState (str fullname))
        [new-title set-title] (react/useState (str title))
        [new-reports-to-person-id set-reports-to-person-id] (react/useState reports-to-person-id)
        [new-start-ts set-start-ts] (react/useState start-ts)
        [new-end-ts set-end-ts] (react/useState end-ts)
        excluded-report-ids (-> (org/collect-reports-ids reports)
                                (conj person-id))
        base-map (assoc (dissoc member
                                :on-save
                                :on-close
                                :abbridged-form
                                :prevent-navigate-on-save)
                        :fullname new-name
                        :title new-title
                        :reports-to-person-id new-reports-to-person-id
                        :is-reports-to-updated (not= reports-to-person-id
                                                     new-reports-to-person-id)
                        :has-reports (-> reports seq boolean)
                        :start-ts new-start-ts
                        :end-ts new-end-ts)
        untouched (and (= member (select-keys base-map (keys member)))
                       (int? person-id))
        close-props {:href (if prevent-navigate-on-save
                             "#"
                             (rfe/href organization-view))
                     :on-click (when (fn? on-close)
                                 (fn [e]
                                   (when prevent-navigate-on-save
                                     (.preventDefault e))
                                   (on-close)))}]

    [:form {:id (keyword :active-user person-id)
            :name (keyword :active-user person-id)
            :on-submit (partial on-submit base-map)}
     [:div.organization-member.tf-nc.notification
      {:ref ref}
      [:a.delete.is-rounded close-props]
      [:div.level.level-left.title
       [:span.icon>i.fas.fa-user.fa-regular.ml-3]
       [:span.ml-5 (or fullname "New Organization Member")]]
      [member-attribute "Name" new-name set-name (:fullname form-errors)]
      [member-attribute "Job Title" new-title set-title (:title form-errors)]
      ;; Employment Controls --
      ;; Hide the start/end and most importantly the reports-to select for the
      ;; root node, which is ALWAYS person-id=0
      (when-not (or (zero? person-id) abbridged-form)
        [:<>
         [member-attribute-wrapper "Reports to"
          [member-select members excluded-report-ids new-reports-to-person-id set-reports-to-person-id]]
         [member-date-input "Start Date" new-start-ts set-start-ts nil new-end-ts]
         [member-date-input "End Date" new-end-ts set-end-ts new-start-ts nil]])
      ;; Revision History & Responsibilities Buttons - sometimes
      (when additional-actions [additional-actions member])
      ;; Action Buttons
      [:div.level
       [:div.buttons.level-right.organization-member-action-buttons
        [:a.button.is-danger
         close-props
         "Cancel"]
        [:button.button.is-success
         {:type "submit"
          :disabled untouched}
         "Save"]]]]]))
